import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Semantic variants (success/warning/info/destructive) are intentionally
// rendered as outlined chips: colored border + colored text, no fill. Filled
// status pills competed visually with primary actions; outlines keep the
// chips informational and let CTAs dominate. `default` / `secondary` stay
// filled because they represent emphasis, not categorical state.
// Fully rounded (`rounded-full`) makes the outlined chips read as pill-shaped
// tags — distinct from rounded-md buttons / inputs / cards around them.
const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-2.5 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 transition-colors',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'text-foreground',
        destructive: 'border-destructive/70 text-destructive',
        success: 'border-success/70 text-success',
        warning: 'border-warning/70 text-warning',
        info: 'border-info/70 text-info',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}
